import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Stack,
  IconButton,
  TextField,
  Paper,
  InputAdornment,
  Avatar,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Badge,
  useTheme,
} from "@mui/material";
import { tokens } from "../theme";
import SendIcon from "@mui/icons-material/Send";
import SearchIcon from "@mui/icons-material/Search";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddCommentIcon from "@mui/icons-material/AddComment";

import PageContainer from "../layouts/PageContainer";
import useDebounce from "../hooks/useDebounce";
import { usePermissions } from "../hooks/usePermissions";
import AppCircularProgress from "../components/common/AppCircularProgress";
import InitiateWhatsappModal from "../components/whatsapp/InitiateWhatsappModal";

import { useAppDispatch, useAppSelector } from "../redux/store";
import {
  getConversations,
  getMessages,
  replyToConversation,
  resolveConversation,
  resetWhatsappServerStatus,
} from "../redux/actions/whatsappActions";
import { whatsappActions } from "../redux/slices/whatsapp";
import { SmsStatus } from "../shared/enums/SmsStatus";

const WhatsappPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { hasPermission } = usePermissions();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const {
    conversations,
    messages,
    activeConversationId,
    loadingConversations,
    loadingMessages,
    loadingReply,
    loadingResolve,
    loadingInitiate,
    errorInitiate: errorInitiating,
    serverStatus,
  } = useAppSelector((state) => state.whatsapp);

  const [searchTerm, setSearchTerm] = useState<string>("");
  const debouncedSearch = useDebounce<string>(searchTerm, 500);
  const [replyText, setReplyText] = useState<string>("");
  const [isInitiateModalOpen, setIsInitiateModalOpen] =
    useState<boolean>(false);

  // Mobile responsive state (hide sidebar when chat is open on small screens)
  const [showSidebarMobile, setShowSidebarMobile] = useState<boolean>(true);

  const messagesEndRef = useRef<HTMLDivElement>(null);

  const canCreate = hasPermission("whatsapp_conversation:create");
  const canUpdate = hasPermission("whatsapp_conversation:update");

  const BORDER_COLOR =
    theme.palette.mode === "dark" ? colors.primary[300] : colors.grey[300];
  const BG_HEADER =
    theme.palette.mode === "dark" ? colors.primary[400] : colors.grey[100];
  const BG_CHAT = theme.palette.background.default;
  const BUBBLE_INBOUND = theme.palette.background.paper;
  const BUBBLE_OUTBOUND = theme.palette.primary.main; // Sky Blue
  const HOVER_BG =
    theme.palette.mode === "dark" ? colors.primary[400] : colors.grey[200];
  const SELECTED_BG =
    theme.palette.mode === "dark" ? colors.primary[300] : colors.blue[100];

  // Fetch conversations
  useEffect(() => {
    dispatch(getConversations({ search: debouncedSearch }));
  }, [dispatch, debouncedSearch]);

  // Scroll to bottom when messages load/update
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  // Handle auto-close for Modal
  useEffect(() => {
    if (
      serverStatus?.operation === "initiate" &&
      serverStatus?.status === 200
    ) {
      setIsInitiateModalOpen(false);
      dispatch(getConversations({ search: "" }));
      dispatch(resetWhatsappServerStatus());
    }
  }, [serverStatus, dispatch]);

  const handleSelectConversation = (id: string): void => {
    dispatch(whatsappActions.setActiveConversationId(id));
    dispatch(getMessages({ conversationId: id, size: 50 }));
    setShowSidebarMobile(false);
  };

  const handleBackToList = (): void => {
    setShowSidebarMobile(true);
    dispatch(whatsappActions.setActiveConversationId(null));
  };

  const handleSendReply = (e: React.FormEvent): void => {
    e.preventDefault();
    if (!replyText.trim() || !activeConversationId) return;
    dispatch(
      replyToConversation({
        conversationId: activeConversationId,
        values: { messageText: replyText },
      }),
    );
    setReplyText("");
  };

  const handleResolve = (): void => {
    if (activeConversationId) {
      dispatch(resolveConversation(activeConversationId));
      setShowSidebarMobile(true);
    }
  };

  // Find active conversation object
  const activeChat = conversations?.find((c) => c.id === activeConversationId);

  // --- RENDERS ---

  const renderSidebar = (): React.JSX.Element => (
    <Box
      sx={{
        width: { xs: "100%", md: "350px" },
        borderRight: { md: `1px solid ${BORDER_COLOR}` },
        display: { xs: showSidebarMobile ? "flex" : "none", md: "flex" },
        flexDirection: "column",
        bgcolor: "background.paper",
      }}
    >
      {/* Search Header */}
      <Box
        sx={{
          p: 2,
          bgcolor: BG_HEADER,
          borderBottom: `1px solid ${BORDER_COLOR}`,
        }}
      >
        <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search or start new chat"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ bgcolor: "background.paper", borderRadius: 1 }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              },
            }}
          />
          {canCreate && (
            <IconButton
              onClick={() => setIsInitiateModalOpen(true)}
              color="primary"
            >
              <AddCommentIcon />
            </IconButton>
          )}
        </Stack>
      </Box>

      {/* List */}
      <Box sx={{ flexGrow: 1, overflowY: "auto" }}>
        {loadingConversations ? (
          <Box sx={{ p: 4, textAlign: "center" }}>
            <AppCircularProgress />
          </Box>
        ) : (
          <List disablePadding>
            {conversations.map((convo) => {
              const isSelected = convo.id === activeConversationId;
              const hasUnreplied = !convo.agentHasResponded;

              return (
                <React.Fragment key={convo.id}>
                  <ListItem
                    onClick={() => handleSelectConversation(convo.id)}
                    sx={{
                      cursor: "pointer",
                      bgcolor: isSelected ? SELECTED_BG : "inherit",
                      "&:hover": { bgcolor: HOVER_BG },
                    }}
                  >
                    <ListItemAvatar>
                      <Badge
                        color="error"
                        variant="dot"
                        invisible={!hasUnreplied}
                      >
                        <Avatar sx={{ bgcolor: "primary.light" }}>
                          {convo.customerName?.charAt(0).toUpperCase() || "?"}
                        </Avatar>
                      </Badge>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Typography
                          sx={{ fontWeight: hasUnreplied ? "bold" : "regular" }}
                          noWrap
                        >
                          {convo.customerName || convo.customerPhoneNumber}
                        </Typography>
                      }
                      secondary={
                        <Typography
                          variant="caption"
                          sx={{ color: "text.secondary" }}
                          noWrap
                        >
                          {convo.customerPhoneNumber}
                        </Typography>
                      }
                    />
                    <Typography
                      variant="caption"
                      sx={{ color: "text.secondary" }}
                    >
                      {new Date(convo.lastActivityAt).toLocaleDateString()}
                    </Typography>
                  </ListItem>
                  <Divider component="li" />
                </React.Fragment>
              );
            })}
            {conversations.length === 0 && (
              <Box sx={{ p: 3, textAlign: "center" }}>
                <Typography sx={{ color: "text.secondary" }}>
                  No conversations found.
                </Typography>
              </Box>
            )}
          </List>
        )}
      </Box>
    </Box>
  );

  const renderChatArea = (): React.JSX.Element => {
    if (!activeConversationId) {
      return (
        <Box
          sx={{
            flexGrow: 1,
            display: { xs: "none", md: "flex" },
            alignItems: "center",
            justifyContent: "center",
            bgcolor: colors.primary[600],
          }}
        >
          <Typography sx={{ color: "text.secondary" }}>
            Select a chat to start messaging
          </Typography>
        </Box>
      );
    }

    return (
      <Box
        sx={{
          flexGrow: 1,
          display: { xs: !showSidebarMobile ? "flex" : "none", md: "flex" },
          flexDirection: "column",
          bgcolor: BG_CHAT,
        }}
      >
        {/* Chat Header */}
        <Box
          sx={{
            p: 2,
            bgcolor: colors.primary[600],
            borderBottom: `1px solid ${BORDER_COLOR}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
            <IconButton
              sx={{ display: { md: "none" } }}
              onClick={handleBackToList}
            >
              <ArrowBackIcon />
            </IconButton>
            <Avatar sx={{ bgcolor: "primary.main" }}>
              {activeChat?.customerName?.charAt(0).toUpperCase() || "?"}
            </Avatar>
            <Box>
              <Typography sx={{ fontWeight: "bold" }}>
                {activeChat?.customerName || "Customer"}
              </Typography>
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                {activeChat?.customerPhoneNumber}
              </Typography>
            </Box>
          </Stack>

          {canUpdate && (
            <IconButton
              color="success"
              onClick={handleResolve}
              disabled={loadingResolve}
              title="Resolve Conversation"
            >
              <CheckCircleOutlineIcon />
            </IconButton>
          )}
        </Box>

        {/* Message Thread */}
        <Box
          sx={{
            flexGrow: 1,
            p: { xs: 1, md: 3 },
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
          }}
        >
          {loadingMessages ? (
            <Box sx={{ textAlign: "center", mt: 5 }}>
              <AppCircularProgress />
            </Box>
          ) : (
            messages.map((msg) => {
              const isOutbound = msg.direction === "OUTBOUND";
              return (
                <Box
                  key={msg.id}
                  sx={{
                    display: "flex",
                    justifyContent: isOutbound ? "flex-end" : "flex-start",
                  }}
                >
                  <Paper
                    elevation={1}
                    sx={{
                      p: 1.5,
                      maxWidth: "75%",
                      bgcolor: isOutbound ? BUBBLE_OUTBOUND : BUBBLE_INBOUND,
                      borderRadius: 2,
                      borderTopRightRadius: isOutbound ? 0 : 2,
                      borderTopLeftRadius: !isOutbound ? 0 : 2,
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{ whiteSpace: "pre-wrap", mb: 0.5 }}
                    >
                      {msg.textBody}
                    </Typography>
                    <Stack
                      direction="row"
                      spacing={0.5}
                      sx={{
                        justifyContent: "flex-end",
                        alignItems: "center",
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{ color: "text.disabled" }}
                      >
                        {new Date(msg.createdAt).toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </Typography>
                      {isOutbound && (
                        <DoneAllIcon
                          sx={{
                            fontSize: 14,
                            color:
                              msg.status === SmsStatus.DELIVERED
                                ? "info.main"
                                : "inherit",
                          }}
                        />
                      )}
                    </Stack>
                  </Paper>
                </Box>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </Box>

        {/* Reply Input */}
        {canUpdate && (
          <Box
            component="form"
            onSubmit={handleSendReply}
            sx={{
              p: 2,
              bgcolor: BG_HEADER,
              borderTop: `1px solid ${BORDER_COLOR}`,
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <TextField
              fullWidth
              size="small"
              placeholder="Type a message..."
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              sx={{
                bgcolor: "background.paper",
                borderRadius: 4,
                "& fieldset": { borderRadius: 4 },
              }}
              disabled={loadingReply || loadingResolve}
            />
            <IconButton
              type="submit"
              color="primary"
              disabled={!replyText.trim() || loadingReply || loadingResolve}
              sx={{
                bgcolor: "primary.main",
                color: "#fff",
                "&:hover": { bgcolor: "primary.dark" },
                "&.Mui-disabled": {
                  bgcolor: "action.disabledBackground",
                  color: "action.disabled",
                },
              }}
            >
              <SendIcon />
            </IconButton>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <PageContainer
      title="WhatsApp Chats"
      subtitle="Manage customer conversations via Whatsapp Business."
    >
      <Paper
        elevation={2}
        sx={{
          display: "flex",
          height: "100%",
          overflow: "hidden",
          borderRadius: 2,
        }}
      >
        {renderSidebar()}
        {renderChatArea()}
      </Paper>

      {/* Init Modal */}
      {isInitiateModalOpen && (
        <InitiateWhatsappModal
          open={isInitiateModalOpen}
          onClose={() => setIsInitiateModalOpen(false)}
          loading={loadingInitiate}
          error={errorInitiating}
        />
      )}
    </PageContainer>
  );
};

export default WhatsappPage;
